import * as microsoftTeams from '@microsoft/teams-js';
import { CLIENT_ID, BASE_SCOPES } from './msal-config';

/**
 * Teams SSO Authentication for the companion tab
 * Uses the same OAuth connection as the bot for seamless SSO
 */

// Re-export CLIENT_ID for backward compatibility
export { CLIENT_ID };

/**
 * Graph scopes needed by the companion app
 * Uses BASE_SCOPES from the phased permission strategy
 */
export const GRAPH_SCOPES = BASE_SCOPES;

/**
 * Initialize the Teams SDK with timeout
 * Must be called before any other Teams SDK operations
 */
export async function initializeTeams(): Promise<boolean> {
  try {
    // Add a timeout to prevent hanging when not in Teams
    const timeoutPromise = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Teams SDK init timeout')), 2000)
    );
    
    await Promise.race([
      microsoftTeams.app.initialize(),
      timeoutPromise
    ]);
    return true;
  } catch (error) {
    console.log('Teams SDK not available (expected outside Teams):', error);
    return false;
  }
}

/**
 * Check if running inside Teams
 * More conservative check to avoid false positives
 */
export function isInTeams(): boolean {
  try {
    // Check for Teams-specific URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    // These params are only added by Teams
    const hasTeamsParams = urlParams.has('inTeams') || 
                           urlParams.has('hostClientType') ||
                           urlParams.has('context');
    
    // Check referrer for Teams domains
    const referrer = document.referrer.toLowerCase();
    const teamsReferrer = referrer.includes('teams.microsoft.com') ||
                          referrer.includes('teams.live.com') ||
                          referrer.includes('.teams.microsoft.com');
    
    return hasTeamsParams || teamsReferrer;
  } catch {
    return false;
  }
}

/**
 * Get the Teams context (user info, theme, etc.)
 */
export async function getTeamsContext(): Promise<microsoftTeams.app.Context | null> {
  try {
    return await microsoftTeams.app.getContext();
  } catch (error) {
    console.error('Failed to get Teams context:', error);
    return null;
  }
}

/**
 * Get an access token using Teams SSO
 * This leverages the same Azure AD app registration as the bot
 */
export async function getTeamsSSOToken(): Promise<string | null> {
  try {
    // First, try silent SSO
    const token = await microsoftTeams.authentication.getAuthToken({
      resources: [`api://botid-${CLIENT_ID}`],
      silent: true,
    });
    return token;
  } catch (error) {
    console.log('Silent SSO failed, trying interactive:', error);
    
    try {
      // Fall back to interactive auth
      const token = await microsoftTeams.authentication.getAuthToken({
        resources: [`api://botid-${CLIENT_ID}`],
        silent: false,
      });
      return token;
    } catch (interactiveError) {
      console.error('Interactive auth failed:', interactiveError);
      return null;
    }
  }
}

/**
 * Exchange the Teams SSO token for a Graph access token
 * Uses the On-Behalf-Of (OBO) flow via your backend
 * 
 * For a simpler approach, you can also use MSAL.js with the Teams token
 */
export async function getGraphToken(_teamsToken: string): Promise<string | null> {
  // Option 1: If you have a backend that does OBO
  // return await exchangeTokenViaBackend(_teamsToken);
  
  // Option 2: Use MSAL.js to acquire token with the Teams identity hint
  // This works because Teams already authenticated the user
  // The token can be used to bootstrap MSAL's cache
  
  // For now, we'll use MSAL directly with popup as fallback
  return null;
}

/**
 * Register theme change handler
 */
export function registerThemeHandler(callback: (theme: string) => void): void {
  microsoftTeams.app.registerOnThemeChangeHandler(callback);
}

/**
 * Get the current Teams theme
 */
export async function getTeamsTheme(): Promise<'default' | 'dark' | 'contrast'> {
  const context = await getTeamsContext();
  return (context?.app?.theme as 'default' | 'dark' | 'contrast') || 'default';
}

/**
 * Notify Teams that the app is loaded
 */
export function notifyAppLoaded(): void {
  microsoftTeams.app.notifyAppLoaded();
}

/**
 * Notify Teams that the app is ready
 */
export function notifySuccess(): void {
  microsoftTeams.app.notifySuccess();
}

